import { useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Trash2 } from 'lucide-react';
import { formatTimeOfDay } from '@vendorops/shared';
import { cn } from '@/lib/utils';
import {
  EDGE_PX,
  EdgeHandles,
  ReadoutPlate,
  type TimelineReadout,
} from '@/components/TimelineGrab';
import {
  BLOCK_TEXT,
  BLOCK_TIME,
  DETAIL_FIELD,
  DETAIL_FIELD_INLINE,
  DetailRow,
  DurationField,
  FieldButton,
  LIFT,
  PANEL_TEXT,
  PANEL_TIME,
  TextField,
  TimeField,
} from '@/components/TimelineFields';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  dayText,
  durationText,
  type TimelineItemDraft,
  type TimelineItemView,
} from '@/components/TimelineItem';
import type { DragKind } from '@/hooks/use-timeline-drag';

/**
 * One part of the day, drawn at its true length and edited where it sits.
 *
 * A fresh component rather than a restyled row: the row's job was to
 * be one rung of a ladder, and every positional decision it made — a fixed
 * clock column, a flowed height, controls in a trailing cell — is the opposite
 * of what a block needs. What carries over is the *facts*, which are the thing
 * that was tested: the three duration answers and the next-day marker. Those
 * come from the same `durationText` the row used.
 *
 * There is no edit mode and no Save button. Each fact on the block is a button
 * that becomes the field that produced it; blur or Enter commits that one
 * field, Escape puts it back. One committed field is one PATCH — a form's worth
 * of writes for a planner who only changed the time is a form, whatever it
 * looks like.
 *
 * Nothing here measures anything. Every number arrives from `layout()` as a
 * prop, which is what makes the axis renderable in a DOM with no layout engine
 * and what lets a drag compute a drop from an injected origin rather than from
 * a box read mid-gesture.
 */

/**
 * Below this a block cannot hold two stacked lines, so it holds one.
 *
 * Seventeen minutes at two pixels a minute. The cutoff is about the text, not
 * about the time: the alternative is clipping the label out of a fifteen-minute
 * item, and a block that cannot say what it is has stopped being a block.
 */
/**
 * The three heights a block can be, and the facts each one can hold.
 *
 * A block is drawn at its true length, so how much room it has is not a
 * styling choice — it is what the planner scheduled. The block spends that
 * room from the top down: the name first, because it is the only fact that
 * says which item this is; then the range; then the length and the note.
 * What does not fit is dropped, not shrunk and not truncated. A range squeezed
 * into a quarter-width lane is not a shorter range, it is an unreadable one,
 * and a note cut to four characters is worse than a note the panel holds
 * whole.
 *
 * Both cutoffs are measured against the *true* height rather than the drawn
 * one. Which layout a block wears is a question about how long it runs; the
 * twenty-eight pixel floor below is a question about what a pointer can hit,
 * and the two should not answer each other.
 */
/**
 * Where the length and the note stop fitting, measured rather than guessed.
 *
 * Three lines — the name, the range, and the length with its note — need about
 * ninety pixels once padding is counted. At two pixels a minute that is three
 * quarters of an hour, and the most ordinary item on a wedding day is half an
 * hour: set this any lower and the commonest block on the page renders a
 * layout it cannot hold, spilling its own length line into the item below.
 */
export const NOTE_HEIGHT_PX = 96;

/**
 * Where the range stops fitting under the name.
 *
 * Two lines — the name at `text-sm` over the range at `text-xs` — come to
 * about fifty-four pixels with the padding. Sixty rather than fifty-four
 * because the half hour is the commonest item on the day and lands exactly on
 * it: a cutoff set by what the text needs, rounded to what the day is made of.
 *
 * Below this a block gets one line, and that line gets the name and the start
 * time. The end time is the first thing to go, and it goes cheaply — where the
 * block stops against the gutter already says when it ends, and the block is
 * drawn at its true length whatever its text is doing.
 */
export const RANGE_HEIGHT_PX = 60;

/**
 * The shortest a card is drawn, whatever its length.
 *
 * One line of text with room to read it. A five-minute item is ten pixels and
 * a moment is none at all, and a card that short is not a card — so below this
 * the drawn box rounds up while `top` stays exact. The cost is that a very
 * short block's bottom edge overstates its end by a few minutes; the gain is
 * that it can be seen, read and grabbed. Every calendar makes this trade, and
 * the length is written on the block in words anyway.
 */
const MIN_CARD_PX = 28;

/** Which fact is currently a field. One at a time: this is not a form. */
type EditableField = 'time' | 'label' | 'duration' | 'note';


export interface TimelineBlockPlacement {
  top: number;
  height: number;
  /** CSS percentages — the lane this block occupies, already subdivided. */
  left: string;
  width: string;
}


/* The three editors, the button that opens one, the row that labels one, and
   the sizes each surface asks them for all live in `TimelineFields` now: a
   coverage window is edited by the same grammar, and the grammar was never
   about blocks. What stays here is what only a block knows — which of its four
   facts fit at its height, and what its popover should say. */

/** The one pointer handler set, handed down so the hook owns the gesture. */
export interface TimelineGrabHandlers {
  onPointerDown: (kind: DragKind, event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
}

/* `EDGE_PX` and the strips themselves live in `TimelineEdges`: three things on
   this axis have a draggable length and there is one answer to how big that
   target is. What stays here is the question only a block can answer — whether
   it has the room for three targets at all. */


export default function TimelineBlock({
  item,
  placement,
  onCommit,
  claimClick,
  onDelete,
  deleting = false,
  grab,
  dragging = false,
  readout = null,
  onNudge,
}: {
  item: TimelineItemView;
  placement: TimelineBlockPlacement;
  /** One committed field, on its way to one PATCH. */
  onCommit: (patch: Partial<TimelineItemDraft>) => void;
  /**
   * Did a drag just end here? Then the click behind it is the drag's own
   * shadow, not a request to open a field.
   *
   * The axis's, not a second rule: `DRAG_THRESHOLD_PX` already decides what
   * counts as movement, and a block that decided it again would eventually
   * disagree with the gesture it is meant to be reading.
   */
  claimClick: () => boolean;
  onDelete: (item: TimelineItemView) => void;
  deleting?: boolean;
  grab: TimelineGrabHandlers;
  /** This block is the one under the pointer. */
  dragging?: boolean;
  readout?: TimelineReadout | null;
  /** One snap step, up or down — the accessible twin of the drag. */
  onNudge: (steps: number, fine: boolean) => void;
}) {
  const { top, height, left, width } = placement;
  const [editing, setEditing] = useState<EditableField | null>(null);
  /* The detail panel, and which of its four facts is currently a field.
     Two pieces of state rather than one, because the panel and the block are
     two places the same fact can be edited from and only one of them may hold
     an open editor at a time: two mounted copies of `TimeField` would both
     autofocus and the second would win. `close()` clears both, so whichever
     surface opened the field, committing it puts every copy back. */
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<EditableField | null>(null);
  /**
   * The pixel the panel hangs off, in offsets from the block's own top-left.
   *
   * A point inside the block rather than a point in the window: the axis
   * scrolls, blocks move under drags, and the panel re-measures its anchor
   * every frame — so a window coordinate would hold still while the block it
   * belongs to slid away from it. An offset inside the block moves with the
   * block for free, because the element carrying it is the block's child.
   *
   * Starts at the block's leading corner, which is where a keyboard opening
   * anchors and where every opening used to.
   */
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  /* Which facts this block has the room for. See `NOTE_HEIGHT_PX`. */
  const tier: 'sliver' | 'short' | 'full' =
    height < RANGE_HEIGHT_PX ? 'sliver' : height < NOTE_HEIGHT_PX ? 'short' : 'full';
  /**
   * Whether the note rides the row the times are on, rather than a line of its
   * own.
   *
   * Every tier below the tall one, and only when there is a note. What a short
   * block is short of is *height* — sixty pixels of block is forty-eight pixels
   * of content, and a third line of text needs about fifty-two — and a block
   * that cannot be given another line can still be given the rest of the one it
   * has. The width is the same width a two-hour block gets. So the note goes
   * beside the times at these heights, truncating where the lane is narrow,
   * which is the same bargain the label on that row already makes.
   *
   * Only when there is a note, because the alternative is "Add a note" printed
   * down the length of a dense day for a field nobody asked for. The block's
   * own body is already one click from the panel that holds the field.
   */
  const noteBesideTime = tier !== 'full' && Boolean(item.note);
  /* What the card is actually drawn at, which is what a pointer can reach.
     A five-minute block is ten pixels of time and twenty-eight pixels of card,
     and gating anything on the ten is gating it on a number nobody can hit. */
  const drawn = Math.max(height, MIN_CARD_PX);
  const clock = formatTimeOfDay(item.clockTime) ?? item.clockTime;
  const day = dayText(item.dayOffset);
  const endClock = item.endClockTime
    ? (formatTimeOfDay(item.endClockTime) ?? item.endClockTime)
    : null;
  /* The marker belongs to whichever end of the range actually crossed a day.
     A 9:30 PM block finishing at 1:00 AM starts on the wedding's own day, so
     marking the start says nothing — it is the *end* that is tomorrow, and
     "9:30 PM – 1:00 AM" with nothing said is the one reading a planner must
     not have to guess at. */
  const endDay =
    item.endDayOffset !== null && item.endDayOffset !== item.dayOffset
      ? dayText(item.endDayOffset)
      : null;
  // Three answers, and `0` is not `null`. See `durationText`.
  const duration = durationText(item.resolvedDurationMinutes);

  /* A pale fill for the body and a solid 1px rule for the edge.
     The edge is not decoration: where a block stops is the length the planner
     reads, so it has to clear 3:1 like any other meaningful graphic — a fill
     alone never can, since a tint pale enough to carry text over white tops out
     around 1.3:1. Measured on the card's white: the rule is 3.25:1 and the fill
     carries `foreground/75` at 5.35:1. Not `text-muted-foreground`, which is
     4.55:1 on bare white but 3.95:1 the moment a tint is laid under it.

     One colour for every block. An item running past the end of the event used
     to be tinted red and to say so underneath; both told a planner what the
     block's own position past the end marker already tells them. */
  /* And the one whose panel is open says so, because nothing else on the axis
     did: a planner with a panel up had no way to tell which of twenty blocks
     it belonged to but the direction it pointed.

     Weight, not hue. The fill cannot carry this on its own — `bg-primary/20`
     takes the block's own secondary text to 4.90:1 against a 4.5 floor, which
     is headroom nobody should be spending on a selection state — and a second
     hue would be the red that got taken off overrunning blocks this morning
     for over-claiming. So: the same sage at full strength, twice as thick,
     over a fill nudged just far enough to read as a change.

     Measured on the card white. Ring `primary` 6.39:1 on white and 5.16:1
     against its own fill, against a 3:1 floor for a graphic that means
     something — which where a block starts and stops does. On the `/15` fill:
     the label 10.31:1, the length and note at `foreground/75` 5.12:1. The
     unselected ring stays 3.61:1. */
  const tint = detailOpen
    ? 'bg-primary/15 ring-2 ring-primary'
    : 'bg-primary/10 ring-primary/75';

  /* Edges only where there is room for three targets. While *this* block is
     being dragged they stay regardless of what the drag has done to its height,
     because a handle that unmounts mid-gesture takes the gesture with it. They
     go entirely while a field is open: an eight-pixel resize strip laid over a
     text input is a caret the planner cannot place. */
  // Two 8px strips and something left in the middle to grab the body by. This
  // is a question about the block's *height*, not about which text layout it
  // wears: a half-hour item is the commonest thing on a wedding day and the
  // one most often stretched to forty-five minutes, and tying its edges to the
  // text tier would have taken that away the moment the tier moved.
  // Measured on the drawn card, not the true height: a block shortened to five
  // minutes is still twenty-eight pixels of card with room for two strips and a
  // body between them, and gating on the true height is what left one with no
  // bottom edge to grab and no way back to any longer length.
  const edges = !editing && (dragging || drawn >= EDGE_PX * 3);

  /**
   * A click on a fact, which is a request for the field behind it — unless a
   * drag just ended on this pixel, in which case the browser is only telling
   * us where the gesture finished.
   */
  const open = (field: EditableField) => {
    if (claimClick()) return;
    setDetail(null);
    setEditing(field);
  };
  /** The same request, made from the panel, where there is no drag to mistake. */
  const openDetail = (field: EditableField) => {
    setEditing(null);
    setDetail(field);
  };
  const close = () => {
    setEditing(null);
    setDetail(null);
  };

  /* Only what actually changed, and only the one field. An unchanged value
     re-sent is a write the planner did not ask for, and the API rejects an
     empty PATCH by design. */
  const commitTime = (clockTime: string) => {
    close();
    if (clockTime === '' || clockTime === item.clockTime) return;
    onCommit({ clockTime });
  };
  const commitLabel = (raw: string) => {
    close();
    const label = raw.trim();
    // An item with no name is not an item, and the schema says so too.
    if (label === '' || label === item.label) return;
    onCommit({ label });
  };
  const commitDuration = (minutes: number | null) => {
    close();
    // The *stored* field, not the resolved one: a trailing block drawn as five
    // hours stores null, and comparing against what it is drawn as would send
    // a number every time the field is opened and closed again.
    if (minutes === item.durationMinutes) return;
    onCommit({ durationMinutes: minutes });
  };
  const commitNote = (raw: string) => {
    close();
    const note = raw.trim() === '' ? null : raw.trim();
    if (note === item.note) return;
    onCommit({ note });
  };

  /* One definition of each field's props, taking the only thing the two
     surfaces disagree about. Whichever size is asked for, the commit it goes
     through is the same one — and `editing` and `detail` are mutually
     exclusive, so only ever one of these is mounted. */
  const timeField = (className: string) => (
    <TimeField
      id={`timeline-${item.id}-time`}
      className={className}
      value={item.clockTime}
      onCommit={commitTime}
      onCancel={close}
    />
  );
  const labelField = (className: string) => (
    <TextField
      name="What happens"
      className={className}
      value={item.label}
      maxLength={120}
      onCommit={commitLabel}
      onCancel={close}
    />
  );
  const durationField = (
    <DurationField
      idPrefix={`timeline-${item.id}-duration`}
      value={item.durationMinutes}
      onCommit={commitDuration}
      onCancel={close}
    />
  );
  const noteField = (className: string) => (
    <TextField
      name="Note"
      className={className}
      value={item.note ?? ''}
      maxLength={500}
      placeholder="Add a note"
      onCommit={commitNote}
      onCancel={close}
    />
  );

  const noteName = item.note ? `Change the note on ${item.label}` : `Add a note to ${item.label}`;
  /**
   * The note as it appears beside the times, on whichever row has the width.
   *
   * A middot and then the planner's own words — the same separator the template
   * card puts between a timeline item's length and its note, because it is the
   * same pair of facts in the same order. Lighter than the times it sits
   * beside: the range is what the block *is*, and the note is what the planner
   * added about it.
   *
   * `min-w-0 truncate` makes it the one thing on the row that gives way. The
   * clock is a fixed width and the label has a floor, so when the lane is
   * narrow the note is what shortens — and a shortened note still says a note
   * is there, which is the whole of what the block owed the planner.
   */
  const noteRun = (
    <>
      <span aria-hidden="true" className="shrink-0 text-foreground/75">
        ·
      </span>
      <FieldButton
        name={noteName}
        className="min-w-0 truncate text-foreground/75"
        onOpen={() => open('note')}
      >
        {item.note}
      </FieldButton>
    </>
  );

  return (
    <li
      data-item-id={item.id}
      /* A block that moves because something else moved travels there rather
         than cutting — a neighbour lengthened, a refused drag rolled back.
         Nothing animates on mount, and nothing animates when a refetch returns
         the same numbers, only a position that actually changed. Off entirely
         for the block under the pointer: a 200ms ease would run the drag a
         fifth of a second behind the finger. */
      className={cn(
        'pointer-events-auto absolute',
        /* Over its neighbours whenever it is the one in play, so a thickened
           ring and a lifted card are not half covered by the block below.
           Listed apart from the transition on purpose: a block with a panel
           open can still be nudged and still be pushed about by an edit to its
           neighbour, and it should travel there like every other block. */
        (dragging || editing || detailOpen) && 'z-10',
        !(dragging || editing) &&
          'transition-[top,height,left,width] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
      )}
      style={{ top, height, left, width }}
    >
      {readout && <ReadoutPlate readout={readout} />}
      {/* The grab surface, and the plate is inside it.
          One element rather than an overlay laid over the text, because the
          text is now made of buttons and an overlay would swallow every one of
          their clicks. Nesting instead gives both: a pointerdown on a field
          still bubbles out to here and arms the drag, and the click that
          follows still reaches the field — the threshold decides which of the
          two the planner meant.

          The element itself never unmounts while a gesture could be running;
          only its children swap as the height crosses a tier cutoff. A
          target that remounted mid-drag would drop the pointer capture it is
          holding. `touch-none` is not optional — without it the browser claims
          the gesture as a scroll and the block never moves on a phone. */}
      <Popover
        open={detailOpen}
        onOpenChange={(next) => {
          setDetailOpen(next);
          // A panel that closes takes its open field with it. Reopening it
          // should show the four facts, not whatever was half typed into one.
          if (!next) setDetail(null);
        }}
      >
        <PopoverTrigger asChild>
          <div
            data-timeline-grab="body"
            /* It was always a focusable target that answered keys; now it also
               opens something, and `PopoverTrigger` puts `aria-haspopup` and
               `aria-expanded` on it. Neither is allowed on a generic div, so
               the role it has been behaving as is the role it now states. */
            role="button"
            tabIndex={0}
            aria-label={`${item.label}, ${endClock ? `${clock} to ${endClock}` : clock}, ${duration}`}
            aria-keyshortcuts="ArrowUp ArrowDown"
            title={
              editing
                ? undefined
                : 'Click for the details, drag to retime, or nudge with the arrow keys'
            }
            className={cn(
              'absolute inset-x-0 top-0 mr-[3px] rounded-xl focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-primary/70',
              editing ? 'cursor-default' : 'cursor-grab touch-none active:cursor-grabbing',
            )}
            style={{ height: drawn }}
            /* Stood down entirely while a field is open, which is the one place
               this rule lives: a press inside an input is a caret being placed,
               and a block dragged out from under an open field would take the
               field's own subject with it. */
            onPointerDown={editing ? undefined : (e) => grab.onPointerDown('body', e)}
            onPointerMove={editing ? undefined : grab.onPointerMove}
            onPointerUp={editing ? undefined : grab.onPointerUp}
            onPointerCancel={editing ? undefined : grab.onPointerCancel}
            /* What the click meant, decided once, here.
               `PopoverTrigger` composes its own toggle after this handler and
               skips it on a prevented default, so this is the whole gate.

               Three things arrive as a click on this element. The shadow a
               finished drag leaves behind, which is the hook's flag and not a
               click at all — consumed here even when it lands on no field, so
               it cannot survive to swallow the next genuine one. A click on a
               fact, which belongs to that fact's field: the facts are buttons
               and the open editors are inputs, and everything else on the
               plate is plain text. And what is left, which is the body, and
               which is the panel's. */
            onClick={(e) => {
              if (claimClick()) {
                e.preventDefault();
                return;
              }
              if (editing || (e.target as HTMLElement).closest('button, input')) {
                e.preventDefault();
                return;
              }
              /* Where the planner actually pointed. The trailing block runs
                 most of the night, and a panel hung off the edge of it opens
                 an hour of screen away from the hand that asked for it.

                 The one box this component reads, and it reads it here
                 because there is nowhere else to read it from: a drag can work
                 from differences of two `clientY` values because it only ever
                 needs a *delta*, and an absolute position inside an element
                 has no second sample to subtract. `offsetX`/`offsetY` would
                 need no measurement at all, but they are relative to whatever
                 was actually under the pointer — a span, a button, the plate —
                 and not to the block. A click is also the one safe moment to
                 measure: the gesture is over and nothing is moving. */
              const box = e.currentTarget.getBoundingClientRect();
              setAnchor({ x: e.clientX - box.left, y: e.clientY - box.top });
            }}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              // An arrow key inside an open field is a caret moving, not a block.
              if (editing) return;
              /* The keyboard's way into the panel. Only from the surface
                 itself: a fact is a real button, so Enter on one already fires
                 that button's click, and a key that reached here from inside
                 one would open the panel over the field it just asked for.
                 A div with `role="button"` gets no synthesised click of its
                 own, so Enter and Space are wired rather than inherited. */
              if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                /* A key press has no cursor to anchor to, so the block's
                   leading edge stands in — the corner the eye already reads
                   the block from, and where the panel used to open from for
                   every opening. */
                setAnchor({ x: 0, y: 0 });
                setDetailOpen(true);
                return;
              }
              const steps = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
              if (steps === 0) return;
              // Or the page scrolls instead, which is the one thing a planner
              // nudging a block by five minutes did not ask for.
              e.preventDefault();
              onNudge(steps, e.altKey);
            }}
          >
            {/* The pixel the panel hangs off: a zero-size point, inside the
                block, at the offset that was clicked.

                A child of the grab surface, and that is load-bearing rather
                than tidy. Until a custom anchor exists `PopoverTrigger` wraps
                itself in a `PopperAnchor` of its own, and the handover is a
                ref callback that only fires on attach — so an anchor that
                merely *precedes* the trigger is overwritten by it and never
                gets the reference back. The panel then opens at the block's
                edge, looking exactly like this feature working. Nested, the
                flip that stops the trigger claiming the reference also
                remounts this element, which re-fires its ref and hands the
                reference over. Measured both ways; there is a test.

                Mounted from the start for the same reason, read the other way
                round: that remount takes the *grab surface* with it, and a
                surface that is replaced the first time a planner opens a panel
                is a surface whose focus and pointer capture go with it.
                Mounted up front, it happens once, before there is anything to
                lose. */}
            <PopoverAnchor asChild>
              <div
                data-timeline-anchor
                aria-hidden="true"
                className="pointer-events-none absolute size-0"
                style={{ left: anchor.x, top: anchor.y }}
              />
            </PopoverAnchor>
            <div
              className={cn(
                /* A hairline off the bottom so two blocks that genuinely touch —
                   10:30 for thirty minutes against an 11:00 start — read as two
                   things rather than one tall one. Taken from the drawn box only;
                   `top` and `height` still carry the real minutes, because they
                   are what the drag converts back into a time. */
                'absolute inset-x-0 top-0 mb-px rounded-xl',
                /* A block twelve pixels tall cannot hold a thirty-pixel input, and
                   growing it to fit would make its height a lie about its length.
                   The field overflows the block instead, over the top of whatever
                   is below it and on its own opaque plate so it stays readable. */
                /* One card, at the block's own height — not a plate floating on a
                   box behind it. Two nested shapes for one item read as a pill
                   stuck to a panel, and the outer one was only ever there because
                   the inner one refused to be as tall as the thing it described. */
                /* And the card is the only thing that clips, which is why the
                   rows below do not: the time picker's menu is absolutely
                   positioned rather than portalled, and it measures the nearest
                   `overflow: hidden` ancestor to decide how tall it can be and
                   which way to open. A row that clipped its own text answered
                   that question with the block's content box, and the menu came
                   up sliced to one row. */
                editing ? 'z-20 bg-card' : 'overflow-hidden',
                (editing || detailOpen) && LIFT,
              )}
              style={editing ? undefined : { height: drawn }}
            >
              <div
                data-timeline-surface
                className={cn('h-full rounded-[inherit] ring-1 ring-inset', tint)}
              >
                {/* Spent from the top down, and what does not fit is dropped.
                    The name is on every tier because it is the only fact that
                    survives having no room: four items at 9:25 share four
                    lanes, and a quarter of a lane that fitted the range and the
                    length pushed the name off the end — four blocks reading
                    identically, with the one thing that told them apart the one
                    thing missing. The range goes first and goes cheaply, since
                    where the block stops against the gutter already says it.
                    The length and the note go next; the panel holds both whole,
                    which a truncated note never is. */}
                {tier === 'sliver' ? (
                  /* One line. The name, the start, and nothing that would push
                     the name into an ellipsis to say something the axis has
                     already said.

                     A floor rather than `min-w-0`, and it is the point rather
                     than a tweak: everything else on this row refuses to
                     shrink, so the name is the only place flexbox can take
                     width from. Given none, it takes all of it, the name goes
                     to nothing and the row starts with the time again. */
                  <div className="flex h-full items-start gap-1.5 whitespace-nowrap px-2 pb-1 pt-1.5 text-xs">
                    {editing === 'label' ? (
                      labelField(BLOCK_TEXT)
                    ) : (
                      <FieldButton
                        name={`Rename ${item.label}`}
                        className="min-w-14 truncate font-medium"
                        onOpen={() => open('label')}
                      >
                        {item.label}
                      </FieldButton>
                    )}
                    {editing === 'time' ? (
                      timeField(BLOCK_TIME)
                    ) : (
                      <FieldButton
                        name={`Change when ${item.label} starts`}
                        className="shrink-0 tabular-nums"
                        onOpen={() => open('time')}
                      >
                        {clock}
                      </FieldButton>
                    )}
                    {/* Stays even here. A bare `1:00 AM` on an axis that opens
                        at midnight is the one time a dropped fact would be read
                        as the wrong answer rather than as no answer — the small
                        hours of the wedding's own date look just like these. */}
                    {day && !editing && (
                      <span className="label-caps shrink-0 text-[0.625rem] text-foreground/75">
                        {day}
                      </span>
                    )}
                    {/* The note, in the planner's own words rather than a mark
                        standing in for them. A twenty-minute block is forty
                        pixels of height and the full width of its lane, and the
                        width is what prose needs — so there is no reading of
                        "too small" on which an icon here was the honest answer.

                        Withdrawn while another field on this row is open, the
                        way the day marker is: one line is one line, and an
                        input is using it. */}
                    {noteBesideTime && editing === 'note' && noteField(BLOCK_TEXT)}
                    {noteBesideTime && !editing && noteRun}
                  </div>
                ) : (
                  /* Two lines, or three. The difference is the last row, so
                     the two tiers are one layout with a tail rather than two
                     layouts that have to be kept looking alike. */
                  <div className="space-y-0.5 px-2.5 py-1.5">
                    <div className="text-sm">
                      {editing === 'label' ? (
                        labelField(BLOCK_TEXT)
                      ) : (
                        <FieldButton
                          name={`Rename ${item.label}`}
                          className="block w-full truncate font-medium"
                          onOpen={() => open('label')}
                        >
                          {item.label}
                        </FieldButton>
                      )}
                    </div>
                    <div
                      className={cn(
                        'flex items-baseline gap-x-1.5 text-xs',
                        /* Wrapping is right for markers, which are short enough
                           that a second line of them still fits the block. It
                           is wrong the moment the row carries a note: a note is
                           as long as the planner made it, and a row that wraps
                           inside a box drawn at a fixed height is a row that
                           leaves the box. One line, truncated, and the panel
                           holds it whole. */
                        noteBesideTime ? 'flex-nowrap' : 'flex-wrap',
                      )}
                    >
                      {/* Tabular figures: the times are a column the planner's
                          eye runs down, and proportional digits make the ladder
                          wobble. */}
                      {editing === 'time' ? (
                        timeField(BLOCK_TIME)
                      ) : (
                        <FieldButton
                          name={`Change when ${item.label} starts`}
                          className="tabular-nums"
                          onOpen={() => open('time')}
                        >
                          {clock}
                        </FieldButton>
                      )}
                      {endClock && editing !== 'time' && (
                        /* The same weight and colour as the start. It is one
                           fact — a range — and setting half of it in a lighter
                           hand reads as the end being less certain than the
                           beginning, which is not something the day is saying.
                           Only the start is a button, because only the start is
                           what the editor changes. */
                        <span className="tabular-nums">– {endClock}</span>
                      )}
                      {endDay && (
                        <span className="label-caps text-[0.625rem] text-foreground/75">{endDay}</span>
                      )}
                      {day && (
                        <span className="label-caps text-[0.625rem] text-foreground/75">{day}</span>
                      )}
                      {/* The note, on the one row this height has to spare, and
                          the same run the one-line layout above shows — a note
                          that moved or changed shape between two neighbouring
                          heights would be a fact the planner has to re-find
                          every time an item is lengthened. */}
                      {noteBesideTime && (editing === 'note' ? noteField(BLOCK_TEXT) : noteRun)}
                    </div>
                    {tier === 'full' && (
                      <div className="flex flex-wrap items-baseline gap-x-1.5 text-xs text-foreground/75">
                        {/* Always a length in words — every item has one of the
                            three answers `durationText` gives — with the
                            planner's own note after it. */}
                        {editing === 'duration' ? (
                          durationField
                        ) : (
                          <FieldButton
                            name={`Change how long ${item.label} runs`}
                            onOpen={() => open('duration')}
                          >
                            {duration}
                          </FieldButton>
                        )}
                        {editing === 'note' ? (
                          noteField(BLOCK_TEXT)
                        ) : (
                          <FieldButton
                            name={noteName}
                            className="min-w-0 truncate"
                            onOpen={() => open('note')}
                          >
                            {item.note ?? 'Add a note'}
                          </FieldButton>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </PopoverTrigger>

        {/* The roomier surface, and the reason it is a portal rather than a
            box drawn next to the block: the axis draws every block inside a
            container with `overflow-y: auto`, and an overflowing ancestor
            clips its descendants however high their z-index. A panel
            positioned by hand would be sliced off at the edge of the
            scroller. Radix renders this into the body. */}
        <PopoverContent
          side="right"
          align="start"
          sideOffset={10}
          collisionPadding={12}
          /* Re-measured every frame while it is up. A block can be dragged
             out from under an open panel, and the default strategy only
             re-places on scroll and resize — which would leave the panel
             pointing at where the block used to be for a whole gesture. */
          updatePositionStrategy="always"
          aria-label={`${item.label} details`}
          /* Escape belongs to the open field first, which is the rule
             everywhere else on the axis: it puts a half-typed value back.
             Radix listens on the document in the capture phase, so without
             this it would close the panel before the field ever saw the key,
             and the planner would lose the edit instead of reverting it. */
          onEscapeKeyDown={(e) => {
            if (detail) e.preventDefault();
          }}
          /* `animate-none!`, because the class it has to beat is
             `data-[state=open]:animate-in`, whose compiled selector carries an
             attribute as well as a class — so it outranks a plain utility
             however late that utility is emitted. */
          className="w-72 rounded-2xl border-[var(--color-outline-variant)] p-0 shadow-[0_12px_36px_rgba(49,51,47,0.16)] motion-reduce:animate-none!"
        >
          {/* The name is the panel's heading and the panel's first field, the
              way a calendar entry's title is. No caps label over it: a title
              that has to be told it is a title is not one. */}
          <div className="px-3.5 pb-3 pt-3.5">
            {detail === 'label' ? (
              labelField(PANEL_TEXT)
            ) : (
              <FieldButton
                name={`Rename ${item.label}`}
                className={cn(DETAIL_FIELD, 'font-medium')}
                onOpen={() => openDetail('label')}
              >
                {item.label}
              </FieldButton>
            )}
          </div>

          <div className="space-y-3 px-3.5 pb-3.5 pt-2.5">
            <DetailRow label="Starts" open={detail === 'time'}>
              {detail === 'time' ? (
                timeField(PANEL_TIME)
              ) : (
                <div className="flex flex-wrap items-baseline gap-x-1.5">
                  <FieldButton
                    name={`Change when ${item.label} starts`}
                    className={cn(DETAIL_FIELD_INLINE, 'tabular-nums')}
                    onOpen={() => openDetail('time')}
                  >
                    {clock}
                  </FieldButton>
                  {endClock && (
                    <span className="text-sm tabular-nums">– {endClock}</span>
                  )}
                  {endDay && (
                    <span className="label-caps text-[0.625rem] text-foreground/75">{endDay}</span>
                  )}
                  {day && (
                    <span className="label-caps text-[0.625rem] text-foreground/75">{day}</span>
                  )}
                </div>
              )}
            </DetailRow>

            <DetailRow label="Length" open={detail === 'duration'}>
              {detail === 'duration' ? (
                durationField
              ) : (
                <FieldButton
                  name={`Change how long ${item.label} runs`}
                  className={DETAIL_FIELD}
                  onOpen={() => openDetail('duration')}
                >
                  {duration}
                </FieldButton>
              )}
            </DetailRow>

            {/* The one field the block cannot hold. A note on a half-hour
                block is truncated to whatever is left of one line after the
                time and the length; here it simply wraps. */}
            <DetailRow label="Note" open={detail === 'note'}>
              {detail === 'note' ? (
                noteField(PANEL_TEXT)
              ) : (
                <FieldButton
                  name={noteName}
                  className={cn(DETAIL_FIELD, item.note ? undefined : 'text-foreground/75')}
                  onOpen={() => openDetail('note')}
                >
                  {item.note ?? 'Add a note'}
                </FieldButton>
              )}
            </DetailRow>
          </div>

          {/* Named, unlike the trash icon on the block. The panel is where a
              planner reads the item rather than points at it, and a delete
              that says which item it is about is the difference between a
              considered removal and an undo. Which the axis still offers. */}
          <div className="border-t border-[var(--color-outline-variant)] p-1.5">
            <button
              type="button"
              disabled={deleting}
              onClick={() => {
                setDetailOpen(false);
                onDelete(item);
              }}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-1 focus-visible:outline-destructive/60 disabled:opacity-50"
            >
              <Trash2 className="size-3.5 shrink-0" aria-hidden="true" />
              Remove {item.label}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* `mr-[3px]` to match the inset the card keeps on its right, which is
          the only thing about these strips that is a block's own business. */}
      {edges && (
        <EdgeHandles
          drawn={drawn}
          className="mr-[3px]"
          onGrab={grab.onPointerDown}
          handlers={{
            onPointerMove: grab.onPointerMove,
            onPointerUp: grab.onPointerUp,
            onPointerCancel: grab.onPointerCancel,
          }}
        />
      )}

    </li>
  );
}



